// 2026-11-02 (Keaton: "an easily accessible place for places ... characters, places, props"): the World panel's data.
// GET /api/projects/:id/places (api places.ts placesView): a video's places and props with their pictures, the
// scenes that use each, and what a new picture costs.

export type PlaceKind = "location" | "prop";
export type PlaceView = {
  id: string;
  kind: PlaceKind;
  name: string;
  description: string;
  assetId?: string;
  url: string | null;
  thumbUrl: string | null;
  /** the scenes (1-based) that use it; explicit = set on the scene, else its picture's description names it */
  scenes: { n: number; explicit: boolean }[];
  /** a story series shares its places with every video in it */
  where: "series" | "video";
  seriesName?: string;
};
export type PlacesView = { locations: PlaceView[]; props: PlaceView[]; prices: { describe: number; fromFrame: number; model: string } };

type Json = Record<string, unknown>;
const isObj = (v: unknown): v is Json => !!v && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown) => (typeof v === "string" ? v : "");

export function normPlace(v: unknown, kind: PlaceKind): PlaceView | null {
  if (!isObj(v) || typeof v.id !== "string" || typeof v.name !== "string") return null;
  return {
    id: v.id,
    kind,
    name: v.name,
    description: str(v.description),
    ...(typeof v.assetId === "string" ? { assetId: v.assetId } : {}),
    url: str(v.url) || null,
    thumbUrl: str(v.thumbUrl) || null,
    scenes: (Array.isArray(v.scenes) ? v.scenes : []).filter(isObj).map((s) => ({ n: Number(s.n) || 0, explicit: s.explicit === true })).filter((s) => s.n > 0),
    where: v.where === "series" ? "series" : "video",
    ...(typeof v.seriesName === "string" ? { seriesName: v.seriesName } : {}),
  };
}

export function normPlaces(v: unknown): PlacesView {
  const o = isObj(v) ? v : {};
  const list = (x: unknown, kind: PlaceKind) => (Array.isArray(x) ? x.map((y) => normPlace(y, kind)).filter((y): y is PlaceView => !!y) : []);
  const pr = isObj(o.prices) ? o.prices : {};
  return { locations: list(o.locations, "location"), props: list(o.props, "prop"), prices: { describe: Number(pr.describe) || 0, fromFrame: Number(pr.fromFrame) || 0, model: str(pr.model) } };
}

/** "Scene 2" / "Scenes 1 and 3" / "Scenes 1–4, 6 and 8" / "Not in a scene yet" (runs of 3+ join with a dash). */
export function scenesLine(scenes: { n: number }[]): string {
  const ns = [...new Set(scenes.map((s) => s.n))].sort((a, b) => a - b);
  if (!ns.length) return "Not in a scene yet";
  if (ns.length === 1) return `Scene ${ns[0]}`;
  const parts: string[] = [];
  for (let i = 0; i < ns.length; ) {
    let j = i;
    while (j + 1 < ns.length && ns[j + 1] === ns[j]! + 1) j++;
    if (j - i >= 2) parts.push(`${ns[i]}\u2013${ns[j]}`);
    else for (let k = i; k <= j; k++) parts.push(String(ns[k]));
    i = j + 1;
  }
  return parts.length === 1 ? `Scenes ${parts[0]}` : `Scenes ${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}`;
}

export const KIND_WORDS: Record<PlaceKind, { one: string; many: string; New: string; empty: string; draw: string }> = {
  location: {
    one: "place",
    many: "Places",
    New: "New place",
    empty: "The rooms, streets and landscapes your scenes are set in. Each one is an empty picture (nobody in it) that every scene set there is drawn from, so the place looks the same every time.",
    draw: "Draw the place",
  },
  prop: {
    one: "prop",
    many: "Props",
    New: "New prop",
    empty: "Objects that come back from scene to scene: a lantern, a letter, a car. Each one is a picture of the thing on its own that every scene showing it is drawn from.",
    draw: "Draw the prop",
  },
};

export type WorldTab = "characters" | PlaceKind;
/** Opens a video's World on a tab from anywhere (a scene's References: "Places and props"); the project screen shows it. */
export const WORLD_EVENT = "cao:world";
export function openWorld(tab: WorldTab = "location") {
  window.dispatchEvent(new CustomEvent(WORLD_EVENT, { detail: { tab } }));
}
